import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, Assignment } from '@mui/icons-material';

const taskTypes = ['Cleaning', 'Maintenance', 'Inspection', 'Laundry'];
const priorities = ['Urgent', 'High', 'Normal', 'Low'];
const statusOptions = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

export default function HousekeepingTaskDialog({ open, onClose, task, onSave, mode = 'add' }) {
  const [formData, setFormData] = useState({
    room: '',
    type: 'Cleaning',
    assignedTo: '',
    priority: 'Normal',
    status: 'Pending',
    startTime: new Date().toTimeString().slice(0, 5),
    estimatedTime: '30 min',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (task && mode === 'edit') {
      setFormData({ ...task });
    } else if (mode === 'add') {
      setFormData({
        room: '',
        type: 'Cleaning',
        assignedTo: '',
        priority: 'Normal',
        status: 'Pending',
        startTime: new Date().toTimeString().slice(0, 5),
        estimatedTime: '30 min',
        notes: '',
      });
    }
  }, [task, mode]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.room.trim()) newErrors.room = 'Room number is required';
    if (!formData.assignedTo.trim()) newErrors.assignedTo = 'Staff assignment is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const taskData = {
      ...formData,
      id: task?.id || `HK${String(Math.floor(Math.random() * 10000)).padStart(3, '0')}`,
    };

    onSave(taskData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      room: '',
      type: 'Cleaning',
      assignedTo: '',
      priority: 'Normal',
      status: 'Pending',
      startTime: new Date().toTimeString().slice(0, 5),
      estimatedTime: '30 min',
      notes: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Assignment color="primary" />
            <Typography variant="h6">
              {mode === 'add' ? 'New Housekeeping Task' : 'Edit Housekeeping Task'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Task Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Room Number"
              value={formData.room}
              onChange={(e) => handleChange('room', e.target.value)}
              error={!!errors.room}
              helperText={errors.room}
              placeholder="e.g., 204"
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Task Type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {taskTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Assigned To"
              value={formData.assignedTo}
              onChange={(e) => handleChange('assignedTo', e.target.value)}
              error={!!errors.assignedTo}
              helperText={errors.assignedTo}
              placeholder="Staff member name"
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Priority"
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
            >
              {priorities.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  {priority}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Start Time"
              type="time"
              value={formData.startTime}
              onChange={(e) => handleChange('startTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Estimated Time"
              value={formData.estimatedTime}
              onChange={(e) => handleChange('estimatedTime', e.target.value)}
              placeholder="e.g., 30 min, 1 hour"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes / Instructions"
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any special instructions or notes for this task..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {mode === 'add' ? 'Create Task' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
